"use client";

import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Hinweissatz } from "../formular/rueckmeldung";
import { Knopf } from "../knopf";
import { stapelTiefe, useModalVerhalten } from "./modal-verhalten";

/*
 * Kleines Modal (Musterkatalog M-008 D6, Design-Guide `detail-formen.md`): Anlegen/Bearbeiten (Größe M) und Rückfrage bzw.
 * Bestätigung (Größe S). Ein Ziel, ein Satz, zwei Knöpfe. Herkunft: `templates/dashboards/admin-dashboard/kanban-board.html`
 * (Detail-Overlays) und `templates/productivity/work-management/projects.html` (zentriertes Modal, `sm:max-w-*`), Vorschau
 * D6a/D6b. Abweichung AW-008 vom Template: Hintergrund mit Blur, React-gesteuert statt `hs-overlay`.
 */

export type KleinesModalErgebnis = { ok: true } | { ok: false; meldung?: string };

/** Der Vorgang; bekommt die Felder des Formulars (`FormData`), wer sie nicht braucht, ignoriert den Parameter. */
export type KleinesModalVorgang = (daten: FormData) => Promise<KleinesModalErgebnis> | KleinesModalErgebnis;

export type KleinesModalGroesse = "s" | "m" | "l";

const BREITE: Record<KleinesModalGroesse, string> = {
  s: "mt-7 opacity-100 sm:max-w-md sm:w-full m-3 sm:mx-auto h-[calc(100%-56px)] min-h-[calc(100%-56px)] flex items-center",
  m: "mt-7 opacity-100 sm:max-w-2xl sm:w-full m-3 sm:mx-auto h-[calc(100%-56px)] min-h-[calc(100%-56px)] flex items-center",
  // Abweichung AW-041: Größe `l` (breites Formular mit Vorschau-Spalte, Besprechungen P3 U2); Quelle: `lg:max-w-4xl` der Blöcke
  // dashboard-/workspace-project-creation-modal. Additiv, `s` und `m` unverändert.
  l: "mt-7 opacity-100 sm:max-w-2xl lg:max-w-4xl sm:w-full m-3 sm:mx-auto h-[calc(100%-56px)] min-h-[calc(100%-56px)] flex items-center",
};


/** Fehlschlag, den der Aufrufer nicht selbst benannt hat (r105: über den Vorgang, wiederholbar, ohne Code). */
const FEHLSCHLAG_STANDARD = "Das hat nicht geklappt. Bitte erneut versuchen.";

/** Lucide `X` (R-F: Lucide, Inline-SVG, das Bausteinpaket führt keine Icon-Abhängigkeit). */
export function SchliessenSymbol() {
  return (
    <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

/** Portal erst nach dem Einhängen (kein `document` beim Server-Rendern). */
export function useEingehaengt(): boolean {
  const [da, setDa] = useState(false);
  useEffect(() => setDa(true), []);
  return da;
}

export function KleinesModal({
  offen,
  groesse = "m",
  titel,
  beschreibung,
  beimSchliessen,
  onBestaetigen,
  bestaetigenText,
  abbrechenText = "Abbrechen",
  gefahr = false,
  bestaetigenGesperrt = false,
  nurSchliessen = false,
  zweiteAktion,
  children,
}: {
  offen: boolean;
  /** `s` = Rückfrage/Bestätigung, `m` = Anlegen/Bearbeiten, `l` = breites Formular mit Vorschau-Spalte (ab `lg` 56 rem). */
  groesse?: KleinesModalGroesse;
  titel: string;
  /** Ein Satz unter dem Titel (z. B. „3 Aufgaben bleiben im Projekt.“). */
  beschreibung?: string;
  /** Schließt das Modal (Esc, Schließen-Knopf, „Abbrechen“ und nach Erfolg). */
  beimSchliessen: () => void;
  /**
   * Der Vorgang. `ok: true` schließt das Modal; `ok: false` lässt es offen, die Eingaben bleiben, der Satz in `meldung`
   * steht im Modal (r98, r105: nie roher Code). Ein Wurf zählt als Fehlschlag.
   */
  onBestaetigen: KleinesModalVorgang;
  bestaetigenText: string;
  abbrechenText?: string;
  /** Löschen/Entfernen: Knopf in Gefahr-Optik (`text-destructive`). */
  gefahr?: boolean;
  bestaetigenGesperrt?: boolean;
  /**
   * Nur lesen (z. B. Termin-Details): kein Bestätigen-Knopf, der Fuß trägt allein `abbrechenText` (dort „Schließen“).
   * `onBestaetigen` und `bestaetigenText` bleiben Pflicht des Typs, werden aber nicht angezeigt.
   */
  nurSchliessen?: boolean;
  /**
   * Eine zweite Aktion neben „Abbrechen“ und dem Haupt-Knopf (Live-Gestaltung C, Dialog „Abstimmung anlegen“: „Anlegen“ neben „Anlegen und öffnen“,
   * Betreiber 17.10.2026 am Bild). Sekundärer Knopf links des Haupt-Knopfes; gleicher Vorgang-Vertrag wie `onBestaetigen` (`ok: true` schließt,
   * `ok: false` lässt offen und zeigt die Meldung), gleicher Doppelklick-Schutz.
   */
  zweiteAktion?: { text: string; onWaehlen: KleinesModalVorgang };
  children?: ReactNode;
}) {
  const eingehaengt = useEingehaengt();
  if (!offen || !eingehaengt) return null;
  return createPortal(
    <Inhalt
      groesse={groesse}
      titel={titel}
      beschreibung={beschreibung}
      beimSchliessen={beimSchliessen}
      onBestaetigen={onBestaetigen}
      bestaetigenText={bestaetigenText}
      abbrechenText={abbrechenText}
      gefahr={gefahr}
      bestaetigenGesperrt={bestaetigenGesperrt}
      nurSchliessen={nurSchliessen}
      zweiteAktion={zweiteAktion}
    >
      {children}
    </Inhalt>,
    document.body,
  );
}

function Inhalt({
  groesse,
  titel,
  beschreibung,
  beimSchliessen,
  onBestaetigen,
  bestaetigenText,
  abbrechenText,
  gefahr,
  bestaetigenGesperrt,
  nurSchliessen,
  zweiteAktion,
  children,
}: {
  groesse: KleinesModalGroesse;
  titel: string;
  beschreibung?: string;
  beimSchliessen: () => void;
  onBestaetigen: KleinesModalVorgang;
  bestaetigenText: string;
  abbrechenText: string;
  gefahr: boolean;
  bestaetigenGesperrt: boolean;
  nurSchliessen: boolean;
  zweiteAktion: { text: string; onWaehlen: KleinesModalVorgang } | undefined;
  children?: ReactNode;
}) {
  const huelle = useRef<HTMLDivElement>(null);
  const id = useId();
  const [sendet, setSendet] = useState(false);
  // Doppelklick-Schutz (Test-Nacharbeit besprechung-aufgabe-ins-brett-nacharbeit, Befund 5): der Zustand `sendet` wird erst nach dem Rendern wahr,
  // ein zweites Submit im selben Takt (Doppelklick, Enter + Klick) sähe noch `false`. Die Ref gilt sofort.
  const laeuft = useRef(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  // Steht beim Öffnen schon ein anderes Modal offen (Link-Dialog des Editors im Serien-Formular), liegt dieses darüber
  // (Hintergrund z-81, Hülle z-82; erstes Modal 70/80, Tour 90), sonst bliebe das erste Modal ungedimmt über dem zweiten Hintergrund.
  // Beim Rendern zählt ein gerade schließendes Modal (Wechsel Anlegen → Serie) noch mit; `beiStapelPlatz` korrigiert nach dessen Aufräumen.
  const [gestapelt, setGestapelt] = useState(() => stapelTiefe() > 0);
  useModalVerhalten({ offen: true, huelle, beimSchliessen, gesperrt: sendet, beiStapelPlatz: (tiefe) => setGestapelt(tiefe > 0) });

  const formular = useRef<HTMLFormElement>(null);

  const fuehreAus = async (vorgang: KleinesModalVorgang, form: HTMLFormElement) => {
    if (sendet || laeuft.current) return;
    laeuft.current = true;
    setSendet(true);
    setMeldung(null);
    try {
      const ergebnis = await vorgang(new FormData(form));
      if (ergebnis.ok) {
        beimSchliessen();
      } else if (ergebnis.meldung) {
        setMeldung(ergebnis.meldung);
      }
    } catch (fehler) {
      console.error("Modal-Vorgang fehlgeschlagen", fehler);
      setMeldung(FEHLSCHLAG_STANDARD);
    } finally {
      laeuft.current = false;
      setSendet(false);
    }
  };

  const absenden = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Das Modal steht per Portal im Baum eines anderen Formulars (Link-Dialog im Serien-Formular): React reicht das Submit-
    // Ereignis sonst an das äußere Formular weiter und speichert es mit.
    e.stopPropagation();
    await fuehreAus(onBestaetigen, e.currentTarget);
  };

  return (
    <>
      {/* Preline: productivity/work-management/projects.html#project-details */}
      <div className={gestapelt ? "fixed inset-0 z-81 bg-foreground/50 backdrop-blur-md" : "fixed inset-0 z-70 bg-foreground/50 backdrop-blur-md"} aria-hidden="true" />
      <div
        ref={huelle}
        className={gestapelt ? "fixed top-0 inset-s-0 size-full z-82 overflow-hidden pointer-events-none" : "fixed top-0 inset-s-0 size-full z-80 overflow-hidden pointer-events-none"}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-titel`}
        aria-describedby={beschreibung ? `${id}-text` : undefined}
        tabIndex={-1}
      >
        <div className={BREITE[groesse]}>
          <form
            ref={formular}
            onSubmit={absenden}
            noValidate
            className="w-full max-h-full flex flex-col bg-overlay border border-overlay-line rounded-xl overflow-hidden pointer-events-auto shadow-xl"
          >
            <div data-modal-kopf className="shrink-0 py-3 px-4 sm:px-6 flex justify-between items-center border-b border-overlay-header">
              <div className="min-w-0">
                <h2 id={`${id}-titel`} className="font-semibold text-foreground">
                  {titel}
                </h2>
                {beschreibung && (
                  <p id={`${id}-text`} className="text-sm text-muted-foreground-1">
                    {beschreibung}
                  </p>
                )}
              </div>
              <Knopf variante="ghost" nurSymbol groesse="sm" label="Schließen (Esc)" symbol={<SchliessenSymbol />} onClick={beimSchliessen} disabled={sendet} />
            </div>

            {(children || meldung) && (
              <div className="grow min-h-0 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-none [&::-webkit-scrollbar-track]:bg-scrollbar-track [&::-webkit-scrollbar-thumb]:bg-scrollbar-thumb">
                <div className="p-4 sm:p-6 space-y-4">
                  {meldung && <Hinweissatz id={`${id}-meldung`}>{meldung}</Hinweissatz>}
                  {children}
                </div>
              </div>
            )}

            <div data-modal-fuss className="shrink-0 py-3 px-4 sm:px-6 border-t border-overlay-footer flex justify-end gap-x-2">
              <Knopf onClick={beimSchliessen} disabled={sendet}>
                {abbrechenText}
              </Knopf>
              {/* Abweichung AW-112 (1): zweite Aktion links des Haupt-Knopfes (Live-Gestaltung C, Dialog „Abstimmung anlegen“) */}
              {!nurSchliessen && zweiteAktion && (
                <Knopf
                  disabled={sendet || bestaetigenGesperrt}
                  onClick={() => {
                    if (formular.current) void fuehreAus(zweiteAktion.onWaehlen, formular.current);
                  }}
                >
                  {zweiteAktion.text}
                </Knopf>
              )}
              {!nurSchliessen && (
                <Knopf type="submit" variante={gefahr ? "gefahr" : "primaer"} groesse="lg" volleBreite disabled={sendet || bestaetigenGesperrt}>
                  {bestaetigenText}
                </Knopf>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
